import React, { useState, useEffect, useMemo } from 'react';
import http from '../Utils/axiosConfig';
import { apiMessage } from "../Utils/apiMessage";
import ToastService from '../Utils/ToastService';
import { isInStandardChart } from '../Utils/Matrix';
import './CustomRateChart.css';

// Blank fat or snf is a wildcard (matches any value) — null, not 0, so
// "not specified" stays distinguishable from a real zero.
const num = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));

// The server stores only {type, fat, snf, rate}, so a generated id wouldn't
// survive a save. (type, fat, snf) is the natural key and what it dedupes on.
const keyOf = (e) => `${e.type}|${num(e.fat)}|${num(e.snf)}`;

const ANY = 'कोणतेही';

const INIT_ENTRY = { type: 'buffalo', fat: '', snf: '', rate: '' };

const TYPE_LABEL = { buffalo: 'म्हैस', cow: 'गाय' };

const STANDARD_RANGE = {
  buffalo: { fat: '5.5 – 15.0', snf: '8.7 – 10.0' },
  cow: { fat: '2.5 – 5.0', snf: '7.5 – 9.0' },
};

// Wildcards sort last within a type, mirroring the lookup order they're used in.
const sortEntries = (list) =>
  [...list].sort(
    (a, b) =>
      a.type.localeCompare(b.type) ||
      (num(a.fat) === null) - (num(b.fat) === null) ||
      (num(a.fat) ?? 0) - (num(b.fat) ?? 0) ||
      (num(a.snf) ?? 0) - (num(b.snf) ?? 0),
  );

const CustomRateChart = () => {
  const [chart, setChart] = useState([]);
  const [savedKey, setSavedKey] = useState(''); // what's currently on the server
  const [newEntry, setNewEntry] = useState(INIT_ENTRY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadChart();
  }, []);

  const loadChart = async () => {
    setLoading(true);
    try {
      const res = await http.post('rate-chart/get');
      if (res?.code === 'DATA_FOUND') {
        const rows = sortEntries(res.data?.chart || []);
        setChart(rows);
        setSavedKey(JSON.stringify(rows));
      } else {
        ToastService.error(apiMessage(res, 'कस्टम रेट चार्ट लोड करताना त्रुटी आली'));
      }
    } catch (err) {
      console.error('Error loading custom rate chart:', err);
      ToastService.error('कस्टम रेट चार्ट लोड करताना त्रुटी आली');
    } finally {
      setLoading(false);
    }
  };

  const saveChart = async () => {
    setSaving(true);
    try {
      const res = await http.post('rate-chart/save', { entries: chart });
      if (res?.code === 'CUSTOM_RATE_SAVED') {
        const rows = sortEntries(res.data?.chart || []);
        setChart(rows);
        setSavedKey(JSON.stringify(rows));
        ToastService.success('कस्टम रेट चार्ट सेव्ह झाले');
      } else {
        // Server names the offending row in data.reason, which apiError prefers.
        ToastService.error(apiMessage(res, 'सेव्ह करताना त्रुटी आली'));
      }
    } catch (err) {
      console.error('Error saving custom rate chart:', err);
      ToastService.error('कस्टम रेट चार्ट सेव्ह करताना त्रुटी आली');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = useMemo(
    () => JSON.stringify(chart) !== savedKey,
    [chart, savedKey],
  );

  const counts = useMemo(
    () => ({
      all: chart.length,
      buffalo: chart.filter((e) => e.type === 'buffalo').length,
      cow: chart.filter((e) => e.type === 'cow').length,
    }),
    [chart],
  );

  const visible = useMemo(
    () => (filter === 'all' ? chart : chart.filter((e) => e.type === filter)),
    [chart, filter],
  );

  // Runs on जोडा / ✓ only. The fat+snf combination decides whether an entry is
  // allowed — rate is the value being set, not part of the key.
  const validate = ({ type, fat, snf, rate }, { ignoreKey } = {}) => {
    const f = num(fat);
    const s = num(snf);
    const r = parseFloat(rate);

    if (f === null && s === null) {
      return 'फॅट किंवा एस.एन.एफ यापैकी किमान एक भरा';
    }
    if ((f !== null && !Number.isFinite(f)) || (s !== null && !Number.isFinite(s)) || !Number.isFinite(r)) {
      return 'कृपया वैध नंबर प्रविष्ट करा';
    }
    if ((f !== null && f <= 0) || (s !== null && s <= 0) || r <= 0) {
      return 'फॅट, एस.एन.एफ व दर शून्यापेक्षा जास्त असावेत';
    }
    // Only an exact entry can collide with the standard chart — a partial rule
    // leaves one field open, and the standard chart is always checked first.
    if (f !== null && s !== null && isInStandardChart({ type, fat: f, snf: s })) {
      return `फॅट ${f} / एस.एन.एफ ${s} स्टॅंडर्ड चार्टमध्ये आधीच आहे — कस्टम दर लागू होणार नाही`;
    }
    const k = keyOf({ type, fat: f, snf: s });
    if (k !== ignoreKey && chart.some((e) => keyOf(e) === k)) {
      return 'या प्रकार, फॅट आणि एस.एन.एफ साठी एंट्री आधीच आहे';
    }
    return null;
  };

  const addEntry = () => {
    const error = validate(newEntry);
    if (error) {
      ToastService.error(error);
      return;
    }
    setChart(
      sortEntries([
        ...chart,
        {
          type: newEntry.type,
          fat: num(newEntry.fat),
          snf: num(newEntry.snf),
          rate: parseFloat(newEntry.rate),
        },
      ]),
    );
    setNewEntry({ ...INIT_ENTRY, type: newEntry.type });
  };

  const deleteEntry = (k) => setChart(chart.filter((e) => keyOf(e) !== k));

  const startEdit = (entry) => {
    setEditingKey(keyOf(entry));
    setEditDraft({ ...entry });
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditDraft(null);
  };

  const commitEdit = () => {
    const error = validate(editDraft, { ignoreKey: editingKey });
    if (error) {
      ToastService.error(error);
      return;
    }
    setChart(
      sortEntries(
        chart.map((e) =>
          keyOf(e) === editingKey
            ? {
                type: editDraft.type,
                fat: num(editDraft.fat),
                snf: num(editDraft.snf),
                rate: parseFloat(editDraft.rate),
              }
            : e,
        ),
      ),
    );
    cancelEdit();
  };

  const range = STANDARD_RANGE[newEntry.type];

  if (loading) {
    return <div className="crc-page"><div className="crc-loading">लोड होत आहे...</div></div>;
  }

  return (
    <div className="crc-page">
      <div className="crc-header">
        <h2>कस्टम रेट चार्ट</h2>
        <p className="crc-subtitle">
          स्टॅंडर्ड चार्टमध्ये नसलेल्या फॅट / एस.एन.एफ साठी तुमचा स्वतःचा दर ठरवा.
          स्टॅंडर्ड चार्टमधील दर यामुळे बदलत नाहीत.
        </p>
      </div>

      {/* Add form */}
      <div className="crc-card">
        <h3 className="crc-card-title">नवीन दर जोडा</h3>
        <div className="crc-form">
          <label className="crc-field">
            <span>प्रकार</span>
            <select
              value={newEntry.type}
              onChange={(e) => setNewEntry({ ...newEntry, type: e.target.value })}
            >
              <option value="buffalo">म्हैस</option>
              <option value="cow">गाय</option>
            </select>
          </label>

          <label className="crc-field">
            <span>फॅट (%)</span>
            <input
              type="number"
              step="any"
              min="0"
              value={newEntry.fat}
              onChange={(e) => setNewEntry({ ...newEntry, fat: e.target.value })}
              placeholder={`उदा. 2.1 / ${ANY}`}
            />
          </label>

          <label className="crc-field">
            <span>एस.एन.एफ (%)</span>
            <input
              type="number"
              step="any"
              min="0"
              value={newEntry.snf}
              onChange={(e) => setNewEntry({ ...newEntry, snf: e.target.value })}
              placeholder={`उदा. 7.2 / ${ANY}`}
            />
          </label>

          <label className="crc-field">
            <span>दर (₹/लि.)</span>
            <input
              type="number"
              step="any"
              min="0"
              value={newEntry.rate}
              onChange={(e) => setNewEntry({ ...newEntry, rate: e.target.value })}
              placeholder="उदा. 24.50"
            />
          </label>

          <button type="button" className="crc-btn crc-btn--add" onClick={addEntry}>
            जोडा
          </button>
        </div>

        <p className="crc-hint">
          {TYPE_LABEL[newEntry.type]} स्टॅंडर्ड चार्ट — फॅट {range.fat}, एस.एन.एफ {range.snf}.
          या मर्यादेबाहेरील मूल्यांसाठीच कस्टम दर लागू होतो.
          <br />
          फॅट किंवा एस.एन.एफ यापैकी एक रिकामे ठेवल्यास तो नियम त्या घटकाच्या
          कोणत्याही मूल्यासाठी लागू होतो. दोन्ही भरलेला नियम आधी तपासला जातो,
          नंतर फक्त-फॅट, नंतर फक्त-एस.एन.एफ.
        </p>
      </div>

      {isDirty && (
        <div className="crc-dirty">
          <span>जतन न केलेले बदल आहेत</span>
          <div className="crc-dirty-actions">
            <button type="button" className="crc-btn crc-btn--ghost" onClick={loadChart} disabled={saving}>
              रद्द करा
            </button>
            <button type="button" className="crc-btn crc-btn--save" onClick={saveChart} disabled={saving}>
              {saving ? 'सेव्ह होत आहे...' : 'सेव्ह करा'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="crc-card">
        <div className="crc-list-head">
          <h3 className="crc-card-title">सध्याचे कस्टम दर</h3>
          <div className="crc-filters">
            {[
              ['all', 'सर्व'],
              ['buffalo', 'म्हैस'],
              ['cow', 'गाय'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`crc-chip ${filter === value ? 'crc-chip--on' : ''}`}
                onClick={() => setFilter(value)}
              >
                {label} <span className="crc-chip-count">{counts[value]}</span>
              </button>
            ))}
          </div>
        </div>

        {visible.length === 0 ? (
          <div className="crc-empty">
            {chart.length === 0
              ? 'अजून कोणताही कस्टम दर जोडलेला नाही.'
              : `${TYPE_LABEL[filter] || ''} साठी कोणताही कस्टम दर नाही.`}
          </div>
        ) : (
          <div className="crc-table-wrap">
            <table className="crc-table">
              <thead>
                <tr>
                  <th>प्रकार</th>
                  <th>फॅट (%)</th>
                  <th>एस.एन.एफ (%)</th>
                  <th>दर (₹/लि.)</th>
                  <th aria-label="क्रिया"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((entry) => {
                  const k = keyOf(entry);
                  const editing = editingKey === k;
                  return (
                    <tr key={k} className={editing ? 'crc-row--editing' : ''}>
                      <td data-label="प्रकार">
                        {editing ? (
                          <select
                            value={editDraft.type}
                            onChange={(e) => setEditDraft({ ...editDraft, type: e.target.value })}
                          >
                            <option value="buffalo">म्हैस</option>
                            <option value="cow">गाय</option>
                          </select>
                        ) : (
                          <span className={`crc-type crc-type--${entry.type}`}>
                            {TYPE_LABEL[entry.type]}
                          </span>
                        )}
                      </td>
                      <td data-label="फॅट (%)">
                        {editing ? (
                          <input
                            type="number"
                            step="any"
                            min="0"
                            placeholder={ANY}
                            value={editDraft.fat ?? ''}
                            onChange={(e) => setEditDraft({ ...editDraft, fat: e.target.value })}
                          />
                        ) : num(entry.fat) === null ? (
                          <span className="crc-any">{ANY}</span>
                        ) : (
                          Number(entry.fat).toFixed(1)
                        )}
                      </td>
                      <td data-label="एस.एन.एफ (%)">
                        {editing ? (
                          <input
                            type="number"
                            step="any"
                            min="0"
                            placeholder={ANY}
                            value={editDraft.snf ?? ''}
                            onChange={(e) => setEditDraft({ ...editDraft, snf: e.target.value })}
                          />
                        ) : num(entry.snf) === null ? (
                          <span className="crc-any">{ANY}</span>
                        ) : (
                          Number(entry.snf).toFixed(1)
                        )}
                      </td>
                      <td data-label="दर (₹/लि.)" className="crc-rate">
                        {editing ? (
                          <input
                            type="number"
                            step="any"
                            min="0"
                            value={editDraft.rate}
                            onChange={(e) => setEditDraft({ ...editDraft, rate: e.target.value })}
                          />
                        ) : (
                          `₹${Number(entry.rate).toFixed(2)}`
                        )}
                      </td>
                      <td data-label="">
                        <div className="crc-actions">
                          {editing ? (
                            <>
                              <button type="button" className="crc-icon crc-icon--ok" onClick={commitEdit} title="ठीक आहे">
                                <i className="fa-solid fa-check"></i>
                              </button>
                              <button type="button" className="crc-icon" onClick={cancelEdit} title="रद्द करा">
                                <i className="fa-solid fa-xmark"></i>
                              </button>
                            </>
                          ) : (
                            <>
                              <button type="button" className="crc-icon" onClick={() => startEdit(entry)} title="संपादन">
                                <i className="fa-solid fa-pen"></i>
                              </button>
                              <button type="button" className="crc-icon crc-icon--del" onClick={() => deleteEntry(k)} title="हटवा">
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomRateChart;
